import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { X } from 'lucide-react';
import { getRequest, postRequest } from "../services/apiService";
import ReusableTable from "../dashboard/ReusableTable";
import ReusableForm from "../common/ReusableForm";
import axios from "axios";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import { Dialog } from "primereact/dialog";
import ReusableDeleteDialog from "../common/ReusableDeleteDialog";
import UpdateTaskPopup from '../master/taskTransaction/UpdateTaskPopup'; // Imported UpdateTaskPopup

const ActionCell = ({ rowData, onEdit, onDelete }) => {
  const op = useRef(null);

  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        op.current.hide();
        onEdit(rowData);
      }
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        op.current.hide();
        onDelete(rowData);
      }
    }
  ];

  return (
    <>
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-rounded p-button-text"
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op} className="p-0 shadow-lg">
        <Menu model={menuItems} className="border-none" />
      </OverlayPanel>
    </>
  );
};

const PertUploadPage = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const toast = useRef(null);
  const [taskTransactions, setTaskTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(1);
  const [selectedShip, setSelectedShip] = useState(12);
  const [totalRecords, setTotalRecords] = useState(0);
  const [file, setFile] = useState(null);
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
  });
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [shipOptions, setShipOptions] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState({
    projects: false,
    ships: false,
  });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    project_id: 1,
    ship_id: 12,
    fileType: 'pert',
  });

  // New state for Add Task functionality
  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState({
    project: "", ship: "", milestone: "", task: "", subtask: "",
    manpower: "", budget: "", contribution_user: "", description: "",
    file: null, taskType: "", taskId: "", unitId: "", subTaskId: "",
    month: "", year: "", userPlanned: "", actualPercent: "",
    actualStart: "", actualEnd: "", start: "", end: ""
  });
  const [milestoneOptions, setMilestoneOptions] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [subtaskOptions, setSubtaskOptions] = useState([]);
  const [formDialogShipOptions, setFormDialogShipOptions] = useState([]);
  const [dropdownLoadingForm, setDropdownLoadingForm] = useState({
    milestones: false, tasks: false, subtasks: false, formDialogShips: false
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone."
    }
  ];

  // State for UpdateTaskPopup visibility
  const [showUpdateTaskPopup, setShowUpdateTaskPopup] = useState(false);

  // States for PERT Confirmation and Inherit Project
  const [showPertConfirmation, setShowPertConfirmation] = useState(false);
  const [pertConfirmationForm, setPertConfirmationForm] = useState({ pertConfirmation: '' });
  const [showInheritProjectDialog, setShowInheritProjectDialog] = useState(false);
  const [inheritProjectForm, setInheritProjectForm] = useState({
    inheritConfirmation: '',
    project_id: null,
    ship_id: null,
    inheritOption: null, // 'full_project' or 'block_wise'
  });
  const [inheritShipOptions, setInheritShipOptions] = useState([]);
  const [dropdownLoadingInherit, setDropdownLoadingInherit] = useState({
    projects: false,
    ships: false,
  });

  const columns = [
    {
      field: "actions",
      header: "Actions",
      body: (row) => (
        <ActionCell rowData={row} onEdit={openEditDialog} onDelete={openDeleteModal} />
      ),
      sortable: false,
      notFilterable: true
    },
    {
      field: "project_name",
      header: "Ship",
      sortable: true
    },
    {
      field: "task_name",
      header: "Task",
      sortable: true,
      body: (rowData) => rowData.task_name || ''
    },
    {
      field: "milestone_name",
      header: "Milestone",
      sortable: true
    },
    {
      field: "actual_start",
      header: "Actual Start",
      sortable: true,
      body: (rowData) => rowData.actual_start ?? 0
    },
    {
      field: "actual_finish",
      header: "Actual Finish",
      sortable: true,
      body: (rowData) => `${rowData.actual_finish ?? ''}`
    },
    {
      field: "actual_completion_percent",
      header: "Actual Completion Percent",
      sortable: true,
      body: (rowData) => `${rowData.actual_completion_percent ?? 0}%`
    },
  ];

  // Form fields for the upload form
  const uploadFormFields = [
    {
      name: "project_id",
      label: "Project",
      type: "select",
      options: projectOptions,
      required: true,
      value: selectedProject,
      onChange: (e) => {
        const projectId = e.value;
        setUploadFormData(prev => ({
          ...prev,
          project_id: projectId,
          ship_id: null
        }));
        fetchShips(projectId);
      }
    },
    {
      name: "ship_id",
      label: "Ship",
      type: "select",
      options: shipOptions,
      required: true,
      disabled: !uploadFormData.project_id || dropdownLoading.ships,
      value: selectedShip
    },
    {
      name: "file",
      label: "Upload File",
      type: "file",
      required: true,
      options: [
        { value: "pert", accept: ".xls,.xlsx,.csv" },
        { value: "tasks", accept: ".xls,.xlsx,.csv" }
      ],
      helpText: "Supported formats: XLS, XLSX, CSV"
    }
  ];

  // Form fields for Add Task form
  const formFields = [
    {
      name: "project",
      label: "Project",
      type: "select",
      required: true,
      options: projectOptions,
      placeholder: "Select Project",
      onChange: (e) => {
        const pid = e.value;
        setFormData(p => ({ ...p, project: pid, ship: "" }));
        setFormDialogShipOptions([]);
        if (pid) fetchDialogShips(pid);
      }
    },
    {
      name: "ship",
      label: "Ship",
      type: "select",
      required: true,
      options: formDialogShipOptions,
      placeholder: "Select Ship",
      disabled: (d) => !d.project || dropdownLoadingForm.formDialogShips,
      loading: dropdownLoadingForm.formDialogShips,
      onChange: (e) => setFormData(p => ({ ...p, ship: e.value }))
    },
    {
      name: "milestone",
      label: "Milestone",
      type: "select",
      required: true,
      options: milestoneOptions,
      loading: dropdownLoadingForm.milestones
    },
    {
      name: "task",
      label: "Task",
      type: "select",
      required: true,
      options: taskOptions,
      loading: dropdownLoadingForm.tasks
    },
    {
      name: "subtask",
      label: "Subtask",
      type: "select",
      required: false,
      options: subtaskOptions,
      loading: dropdownLoadingForm.subtasks
    },
    { name: "manpower", label: "Manpower", type: "number" },
    { name: "budget", label: "Budget", type: "number" },
    { name: "contribution_user", label: "Contribution (%)", type: "number" },
    { name: "description", label: "Description", type: "textarea" },
    { name: "file", label: "File", type: "file" },
    { name: "taskType", label: "Task Type", type: "text" },
    { name: "taskId", label: "Task ID", type: "text" },
    { name: "unitId", label: "Unit ID", type: "text" },
    { name: "subTaskId", label: "Sub Task ID", type: "text" },
    {
      name: "month",
      label: "Month",
      type: "select",
      options: [
        { label: "January", value: "01" }, { label: "February", value: "02" },
        { label: "March", value: "03" }, { label: "April", value: "04" },
        { label: "May", value: "05" }, { label: "June", value: "06" },
        { label: "July", value: "07" }, { label: "August", value: "08" },
        { label: "September", value: "09" }, { label: "October", value: "10" },
        { label: "November", value: "11" }, { label: "December", value: "12" }
      ]
    },
    {
      name: "year",
      label: "Year",
      type: "select",
      options: Array.from({ length: 11 }, (_, i) => ({
        label: String(2020 + i), value: String(2020 + i)
      }))
    },
    { name: "userPlanned", label: "User Planned %", type: "number" },
    { name: "actualPercent", label: "Actual %", type: "number" },
    { name: "actualStart", label: "Actual Start", type: "date" },
    { name: "actualEnd", label: "Actual End", type: "date" },
    { name: "start", label: "Start", type: "date" },
    { name: "end", label: "End", type: "date" }
  ];

  const pertConfirmationFields = [
    {
      name: 'pertConfirmation',
      label: 'Do you have a PERT document?',
      type: 'select',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ],
      required: true,
      placeholder: 'Select Yes or No',
      helpText: 'A PERT (Program Evaluation and Review Technique) document is required before proceeding with the upload.'
    }
  ];

  const inheritProjectFields = [
    {
      name: 'inheritConfirmation',
      label: 'Do you want to inherit from any project?',
      type: 'select',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ],
      required: true,
      placeholder: 'Select Yes or No',
      onChange: (e) => setInheritProjectForm(prev => ({ ...prev, inheritConfirmation: e.value }))
    },
    {
      name: "project_id",
      label: "Project",
      type: "select",
      options: projectOptions, // Use the already fetched projectOptions
      required: true,
      visible: (formData) => formData.inheritConfirmation === 'yes',
      onChange: (e) => {
        const projectId = e.value;
        setInheritProjectForm(prev => ({
          ...prev,
          project_id: projectId,
          ship_id: null // Reset ship_id when project changes
        }));
        fetchInheritShips(projectId);
      }
    },
    {
      name: "ship_id",
      label: "Ship",
      type: "select",
      options: inheritShipOptions,
      required: true,
      visible: (formData) => formData.inheritConfirmation === 'yes' && formData.project_id,
      disabled: (formData) => !formData.project_id || dropdownLoadingInherit.ships,
      loading: dropdownLoadingInherit.ships,
      onChange: (e) => setInheritProjectForm(prev => ({ ...prev, ship_id: e.value }))
    },
    {
      name: "inheritOption",
      label: "Inherit Option",
      type: "select",
      options: [
        { label: 'Full Project Inherit', value: 'full_project' },
        { label: 'Inherit Block Wise', value: 'block_wise' }
      ],
      required: true,
      visible: (formData) => formData.inheritConfirmation === 'yes' && formData.project_id && formData.ship_id,
      placeholder: 'Select Inherit Option',
      onChange: (e) => setInheritProjectForm(prev => ({ ...prev, inheritOption: e.value }))
    },
  ];


  const fetchTaskTransactions = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      let url = `/transaction/task-transaction?page=${page}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await getRequest(url);
      if (response.results?.data) {
        const transactions = response.results.data.map(transaction => ({
          ...transaction,
          project_name: transaction.project?.name || `Project ${transaction.project?.id || transaction.project}`,
          task_name: transaction.task?.name || '',
          milestone_name: transaction.milestone?.title || ``,
        }));

        setTaskTransactions(transactions);
        setTotalRecords(response.count || 0);
        setLazyState(prev => ({
          ...prev,
          first: (page - 1) * pageSize,
          page
        }));
      }
    } catch (error) {
      console.error("Error fetching task transactions:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch task transactions",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setDropdownLoading(prev => ({ ...prev, projects: true }));
      setDropdownLoadingInherit(prev => ({ ...prev, projects: true }));
      try {
        const response = await getRequest("/master/projects/");
        // Assuming the response structure for projects is response.data
        const projectData = response.data || response.results?.data || [];
        const projectOpts = projectData.map(proj => ({
          label: proj.name,
          value: proj.id
        }));

        const defaultProjectExists = projectData.some(proj => proj.id === 1);
        if (!defaultProjectExists) {
          console.error("Default project (ID 1) not found in projects list");
        }

        setProjectOptions(projectOpts);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setDropdownLoading(prev => ({ ...prev, projects: false }));
        setDropdownLoadingInherit(prev => ({ ...prev, projects: false }));
      }
    };
    fetchProjects();
  }, []);

  const fetchShips = async (projectId) => {
    if (!projectId) {
      setShipOptions([]);
      return;
    }
    try {
      setDropdownLoading(prev => ({ ...prev, ships: true }));
      const response = await getRequest(`/master/projects/?parent_id=${projectId}`);
      // Assuming the response structure for ships is response.data
      const shipData = response.data || response.results?.data || [];
      const opts = shipData.map(ship => ({
        label: ship.name,
        value: ship.id
      }));

      const defaultShip = shipData.find(ship => ship.id === 12);
      if (defaultShip) {
        setSelectedShip(12);
        setSearch(defaultShip.name);
      } else {
        console.error("Default ship (ID 12) not found in ships list");
        setSelectedShip(null);
      }

      setShipOptions(opts);
    } catch (error) {
      console.error("Error fetching ships:", error);
      setShipOptions([]);
    } finally {
      setDropdownLoading(prev => ({ ...prev, ships: false }));
    }
  };

  const fetchInheritShips = async (projectId) => {
    if (!projectId) {
      setInheritShipOptions([]);
      return;
    }
    try {
      setDropdownLoadingInherit(prev => ({ ...prev, ships: true }));
      const response = await getRequest(`/master/projects/?parent_id=${projectId}`);
      // Assuming the response structure for ships is response.data
      const shipData = response.data || response.results?.data || [];
      const opts = shipData.map(ship => ({
        label: ship.name,
        value: ship.id
      }));
      setInheritShipOptions(opts);
    } catch (error) {
      console.error("Error fetching inherit ships:", error);
      setInheritShipOptions([]);
    } finally {
      setDropdownLoadingInherit(prev => ({ ...prev, ships: false }));
    }
  };

  // New fetch functions for Add Task form
  const fetchDialogShips = async (projectId) => {
    if (!projectId) return;
    setDropdownLoadingForm(d => ({ ...d, formDialogShips: true }));
    try {
      const res = await getRequest(`/master/projects/?parent_id=${projectId}`);
      const ships = res.data || res.results?.data || [];
      setFormDialogShipOptions(ships.map(s => ({ label: s.name, value: s.id })));
    } catch {
      setFormDialogShipOptions([]);
    } finally {
      setDropdownLoadingForm(d => ({ ...d, formDialogShips: false }));
    }
  };

  const fetchFormDropdowns = async () => {
    setDropdownLoadingForm(d => ({ ...d, milestones: true, tasks: true }));
    try {
      const [msRes, taskRes] = await Promise.all([
        getRequest("/master/milestones/"),
        getRequest("/master/tasks/")
      ]);

      setMilestoneOptions((msRes.data || msRes.results?.data).map(m => ({
        label: m.title || m.name, value: m.id
      })));

      setTaskOptions((taskRes.data || taskRes.results?.data).map(t => ({
        label: t.name, value: t.id
      })));

      setSubtaskOptions((taskRes.data || taskRes.results?.data).map(t => ({
        label: t.name, value: t.id
      })));
    } catch (err) {
      toast.current?.show({
        severity: "error", summary: "Error", detail: "Dropdown loading failed", life: 3000
      });
    } finally {
      setDropdownLoadingForm(d => ({ ...d, milestones: false, tasks: false }));
    }
  };

  useEffect(() => {
    fetchShips(1);
    fetchFormDropdowns();
  }, []);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchTaskTransactions(lazyState.page, lazyState.rows);
    }, 700);
    return () => clearTimeout(searchTimeout.current);
  }, [search, lazyState.page]);

  const handleProjectChange = (e) => {
    const projectId = e.value;
    setSelectedProject(projectId);
    setSelectedShip(null);
    setShipOptions([]);
    setSearch("");
    fetchShips(projectId);
  };

  const handleShipChange = (e) => {
    const shipId = e.value;
    const ship = shipOptions.find(opt => opt.value === shipId);
    setSelectedShip(shipId);
    setSearch(ship?.label || "");
  };

  const handleUploadClick = () => {
    setPertConfirmationForm({ pertConfirmation: '' });
    setShowPertConfirmation(true);
  };

  const handlePertConfirmation = (confirmed) => {
    setShowPertConfirmation(false);
    if (confirmed) {
      // If 'Yes', show the 'Upload Tasks' popup
      setUploadFormData({
        project_id: selectedProject,
        ship_id: selectedShip,
        file: null,
        fileType: 'pert'
      });
      setShowUploadForm(true);
    } else {
      // If 'No', show the 'Do you want to inherit from any project?' popup
      setInheritProjectForm(prev => ({ ...prev, inheritConfirmation: '', project_id: null, ship_id: null, inheritOption: null }));
      setShowInheritProjectDialog(true);
    }
  };

  const handleInheritProjectSubmit = () => {
    // This function will be called when the user submits the "Inherit Project" form
    setShowInheritProjectDialog(false);
    if (inheritProjectForm.inheritConfirmation === 'yes') {
      toast.current?.show({
        severity: 'info',
        summary: 'Inherit Project',
        detail: `Inherit option selected: ${inheritProjectForm.inheritOption} from Project ID: ${inheritProjectForm.project_id}, Ship ID: ${inheritProjectForm.ship_id}`,
        life: 5000
      });
      // Add further logic here based on 'full_project' or 'block_wise' inheritance
      // This might involve making another API call or updating state for further actions.
    } else {
      toast.current?.show({
        severity: 'info',
        summary: 'Inherit Project',
        detail: `User chose not to inherit from any project.`,
        life: 3000
      });
      // Handle the case where user chose not to inherit
    }
  };

  const handleUploadFormClose = () => {
    setShowUploadForm(false);
  };

  const handleUploadFormSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      showErrorToast('Please select a file to upload');
      return;
    }

    if (!uploadFormData.project_id) {
      showErrorToast('Project ID is required');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('project_id', uploadFormData.ship_id);

      const pertResponse = await postRequest(`pert/upload-pert/`, formData, true);

      toast.current?.show({
        severity: 'success',
        summary: 'Upload Successful',
        detail: 'File uploaded successfully',
        life: 3000
      });

      setFile(null);
      setUploadFormData({ project_id: '' });
      fetchTaskTransactions(lazyState.page, lazyState.rows);
      setShowUploadForm(false); // Close the upload form after successful upload

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || 'File upload failed';
      showErrorToast(errorMessage);
    }
  };

  const showErrorToast = (message) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 3000
    });
  };

  const handleFileChange = e => {
    const picked = e.target.files[0];
    if (!picked) return;
    setFile(picked);
    setUploadFormData(prev => ({ ...prev, file: picked }));
  };

  // New handlers for Add Task functionality
  const openAddDialog = () => {
    setFormData({
      project: "", ship: "", milestone: "", task: "", subtask: "",
      manpower: "", budget: "", contribution_user: "", description: "",
      file: null, taskType: "", taskId: "", unitId: "", subTaskId: "",
      month: "", year: "", userPlanned: "", actualPercent: "",
      actualStart: "", actualEnd: "", start: "", end: ""
    });
    setSelectedTransaction(null);
    setShowFormModal(true);
    setFormDialogShipOptions([]);
    fetchFormDropdowns();
  };

  const openEditDialog = async (tr) => {
    setSelectedTransaction(tr);
    const pid = tr.project?.parent?.id || tr.project?.id;
    const sid = tr.project?.parent ? tr.project?.id : "";
    const mid = tr.milestone?.id || "";
    const pTid = tr.task?.parent_task?.id || "";
    const stid = pTid ? tr.task?.id : "";
    const task = pTid ? pTid : tr.task?.id || "";

    setFormData({
      project: pid || "",
      ship: sid || "",
      milestone: mid || "",
      task: task || "",
      subtask: stid || "",
      manpower: tr.manpower || "",
      budget: tr.budget || "",
      contribution_user: tr.contribution_user || "",
      description: tr.description || "",
      file: null,
      taskType: tr.taskType || "",
      taskId: task,
      unitId: tr.project?.id || "",
      subTaskId: stid,
      month: tr.month || "",
      year: tr.year || "",
      userPlanned: tr.userPlanned || "",
      actualPercent: tr.actualPercent || "",
      actualStart: tr.actualStart || "",
      actualEnd: tr.actualEnd || "",
      start: tr.start || "",
      end: tr.end || ""
    });

    if (pid) await fetchDialogShips(pid);
    setShowFormModal(true);
  };

  const openDeleteModal = (row) => {
    setTaskToDelete(row);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({ severity: "error", summary: "Error", detail: "Type DELETE to confirm", life: 3000 });
      return;
    }
    try {
      await postRequest(`/transaction/task-transaction/${taskToDelete.id}/delete/`);
      toast.current.show({ severity: "success", summary: "Deleted", detail: "Task transaction removed", life: 3000 });
      if (taskTransactions.length === 1 && lazyState.page > 1)
        setLazyState(l => ({ ...l, page: l.page - 1 }));
      else fetchTaskTransactions(lazyState.page, lazyState.rows);
      setShowDeleteModal(false);
    } catch {
      toast.current.show({ severity: "error", summary: "Error", detail: "Delete failed", life: 3000 });
    }
  };

  const handleSaveTransaction = async () => {
    if (!formData.project || !formData.milestone || !formData.task) {
      toast.current.show({ severity: "error", summary: "Error", detail: "Required fields missing", life: 3000 });
      return;
    }
    const payload = {
      project: formData.ship,
      milestone: formData.milestone,
      task: formData.subtask,
      manpower: formData.manpower,
      budget: formData.budget,
      contribution_user: formData.contribution_user,
      description: formData.description,
      file: formData.file,
      taskType: formData.taskType
    };
    try {
      if (selectedTransaction) {
        await postRequest(`/transaction/task-transaction/${selectedTransaction.id}/update/`, payload);
        toast.current.show({ severity: "success", summary: "Updated", detail: "Task updated", life: 3000 });
      } else {
        await postRequest("/transaction/task-transaction", payload);
        toast.current.show({ severity: "success", summary: "Added", detail: "Task added", life: 3000 });
      }
      fetchTaskTransactions(lazyState.page, lazyState.rows);
      setShowFormModal(false);
    } catch {
      toast.current.show({ severity: "error", summary: "Error", detail: "Save failed", life: 3000 });
    }
  };

  return (
    <div className="p-4 overflow-x-hidden">
      <Toast ref={toast} position="top-right" />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[200px] max-w-[300px] relative">
          <Dropdown
            value={selectedProject}
            options={projectOptions}
            onChange={handleProjectChange}
            placeholder={dropdownLoading.projects ? "Loading Projects..." : "Select Project"}
            disabled={dropdownLoading.projects}
            className="w-full"
          />
          {dropdownLoading.projects && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-[200px] max-w-[300px] relative">
          <Dropdown
            value={selectedShip}
            options={shipOptions}
            onChange={handleShipChange}
            placeholder={dropdownLoading.ships ? "Loading Ships..." : "Select Ship"}
            disabled={!selectedProject || dropdownLoading.ships}
            className="w-full"
          />
          {dropdownLoading.ships && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
            </div>
          )}
        </div>

        {/* Add Task button */}
        <Button
          label="Add Task"
          icon="pi pi-plus"
          onClick={openAddDialog}
          className="p-button-primary border-2 px-3 text-white bg-gradient-to-r from-blue-500 to-blue-600"
        />

        <Button
          label="Upload Pert"
          icon="pi pi-upload"
          onClick={handleUploadClick}
          className="p-button-success border-2 text-white bg-gradient-to-r from-green-500 to-green-600 rounded-lg px-4 py-2 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg flex items-center gap-3 font-medium"
          iconPos="right"
        />

        {/* New Update Task Button */}
        <Button
          label="Update Task"
          icon="pi pi-pencil"
          onClick={() => setShowUpdateTaskPopup(true)} // Opens the popup
          className="p-button-info border-2 text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg px-4 py-2 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg flex items-center gap-3 font-medium"
          iconPos="right"
        />
      </div>

      <div className="overflow-x-auto">
        <ReusableTable
          title={<h2 className="text-xl font-bold">Pert Transactions</h2>}
          data={taskTransactions}
          columnFields={columns}
          rows={lazyState.rows}
          loading={loading}
          colorScheme={{
            headerBgColor: '#1e3a8a',
            headerTextColor: '#ffffff',
            tableBgColor: '#ffffff',
            tableTextColor: '#334155',
            uploadButtonColor: '#f59e0b'
          }}
          totalRecords={totalRecords}
          onPageChange={(e) => setLazyState(prev => ({
    ...prev,
    first: e.first,
    rows: e.rows,
    page: e.page + 1 // Increment by 1 to make it 1-based
}))}
          first={lazyState.first}
          searchValue={search}
          onSearchChange={setSearch}
        />
      </div>

      {/* PERT Confirmation Modal */}
      <Dialog
        visible={showPertConfirmation}
        header="PERT Confirmation"
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        className="max-w-md w-full mx-auto"
        onHide={() => setShowPertConfirmation(false)}
        modal
        contentClassName="p-0"
      >
        <ReusableForm
          formData={pertConfirmationForm}
          setFormData={setPertConfirmationForm}
          fields={pertConfirmationFields}
          onSubmit={() => {
            handlePertConfirmation(pertConfirmationForm.pertConfirmation === 'yes');
            setPertConfirmationForm({ pertConfirmation: '' }); // Reset form after submission
          }}
          onHide={() => setShowPertConfirmation(false)}
          submitLabel="Continue"
        />
      </Dialog>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl animate-fade-in flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] text-white rounded-t-xl border-b border-gray-200 sticky top-0 z-10">
              <h3 className="text-xl font-bold tracking-wide">Upload Tasks</h3>
              <button onClick={handleUploadFormClose} className="text-white hover:text-gray-300">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <ReusableForm
                formData={uploadFormData}
                setFormData={setUploadFormData}
                fields={uploadFormFields.map(field => {
                  if (field.name === 'file') {
                    return {
                      ...field,
                      onChange: (e) => handleFileChange(e)
                    };
                  }
                  return field;
                })}
                onSubmit={handleUploadFormSubmit}
                onHide={handleUploadFormClose}
                submitLabel="Upload"
                loading={false}
                buttonClassName="bg-blue-600 hover:bg-blue-700"
              />
            </div>
          </div>
        </div>
      )}

      {/* Inherit Project Dialog */}
      <Dialog
        visible={showInheritProjectDialog}
        header="Inherit Project Options"
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        className="max-w-xl w-full mx-auto"
        onHide={() => setShowInheritProjectDialog(false)}
        modal
        contentClassName="p-0"
      >
        <ReusableForm
          formData={inheritProjectForm}
          setFormData={setInheritProjectForm}
          fields={inheritProjectFields}
          onSubmit={handleInheritProjectSubmit}
          onHide={() => setShowInheritProjectDialog(false)}
          submitLabel="Continue"
          loading={dropdownLoadingInherit.projects || dropdownLoadingInherit.ships}
        />
      </Dialog>

      {/* Add/Edit Task Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl animate-fade-in flex flex-col">
            <ReusableForm
              headerText={selectedTransaction ? "Edit Task Transaction" : "Add Task Transaction"}
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onSubmit={handleSaveTransaction}
              onHide={() => setShowFormModal(false)}
              loading={loading}
            />
          </div>
        </div>
      )}

      {/* Update Task Popup Modal */}
      {showUpdateTaskPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl animate-fade-in flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] text-white rounded-t-xl border-b border-gray-200 sticky top-0 z-10">
              <h3 className="text-xl font-bold tracking-wide">Update Task</h3>
              <button onClick={() => setShowUpdateTaskPopup(false)} className="text-white hover:text-gray-300">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <UpdateTaskPopup
                onClose={() => setShowUpdateTaskPopup(false)} // Example close handler for the popup itself
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Task Transaction"
        description={`Are you sure you want to delete "${taskToDelete?.task_name}"? Type DELETE to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default PertUploadPage;